import { useEffect, useState, type MouseEvent } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DollarSign, Cpu, FilePlus2, PlusCircle } from 'lucide-react';
import { formatCompactCurrency } from '@/lib/utils';
import { QuotePriorityBadge } from './QuotePriorityBadge';
import type { ProjectKanbanCard as ProjectKanbanCardType } from '../types';

interface ProjectKanbanCardProps {
  project: ProjectKanbanCardType;
  onClick?: () => void;
  onCreateInvoice?: () => void;
  isDragging?: boolean;
}

function formatPersonName(name?: string): string {
  if (!name) return '';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1][0]?.toUpperCase();
  return lastInitial ? `${firstName} ${lastInitial}` : firstName;
}

function getQuoteDisplayName(project: ProjectKanbanCardType): string {
  const quoteTitle = (project as ProjectKanbanCardType & { quoteTitle?: string; quote_title?: string }).quoteTitle;
  const quoteTitleSnake = (project as ProjectKanbanCardType & { quote_title?: string }).quote_title;
  return quoteTitle || quoteTitleSnake || project.projectName || 'Untitled Quote';
}

export function ProjectKanbanCard({
  project,
  onClick,
  onCreateInvoice,
  isDragging: isDraggingProp,
}: ProjectKanbanCardProps) {
  const [contextMenuPosition, setContextMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: project.id });

  const dragging = isDraggingProp || isSortableDragging;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  const quoteDisplayName = getQuoteDisplayName(project);

  useEffect(() => {
    if (!contextMenuPosition) return;

    const closeMenu = () => setContextMenuPosition(null);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.addEventListener('click', closeMenu);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('blur', closeMenu);
    window.addEventListener('scroll', closeMenu, true);

    return () => {
      document.removeEventListener('click', closeMenu);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('blur', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, [contextMenuPosition]);

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (!onCreateInvoice) return;

    event.preventDefault();
    event.stopPropagation();
    setContextMenuPosition({
      x: Math.min(event.clientX, window.innerWidth - 190),
      y: Math.min(event.clientY, window.innerHeight - 48),
    });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`group rounded-lg border bg-card p-3 shadow-sm cursor-grab active:cursor-grabbing select-none ${
        dragging ? 'opacity-50 shadow-lg ring-2 ring-primary' : 'hover:shadow-md'
      }`}
      onContextMenu={handleContextMenu}
      onClick={(e) => {
        if (!dragging && onClick) {
          e.stopPropagation();
          onClick();
        }
      }}
    >
      {/* Header: quote number + priority */}
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold truncate">#{project.quoteNumber || 'No quote #'}</span>
        <QuotePriorityBadge priority={project.priority} />
      </div>

      {/* Project title */}
      <p className="text-xs font-medium truncate mb-0.5">{quoteDisplayName}</p>

      {/* Account name */}
      <p className="text-xs text-muted-foreground truncate mb-2">{project.accountName || 'No Account'}</p>

      {/* Metrics: sensors qty + value */}
      <div className="flex items-center gap-3 mb-2">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Cpu className="h-3 w-3" />
          <span>{project.sensorsQty} qty</span>
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <DollarSign className="h-3 w-3" />
          <span>{formatCompactCurrency(project.amount)}</span>
        </div>
      </div>

      {/* Footer: ops + AM */}
      <div className="text-[11px] text-muted-foreground space-y-0.5">
        <p className="truncate">Ops: {formatPersonName(project.assignedToName) || 'Unassigned'}</p>
        {project.accountManagerName && (
          <p className="truncate">AM: {formatPersonName(project.accountManagerName)}</p>
        )}
        {!project.supportProjectId && (
          <p className="flex items-center gap-1 truncate text-primary opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition">
            <PlusCircle className="h-3 w-3 shrink-0" />
            <span className="truncate">Add supporting project</span>
          </p>
        )}
      </div>

      {contextMenuPosition && (
        <div
          role="menu"
          className="fixed z-50 min-w-[180px] rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          style={{ left: contextMenuPosition.x, top: contextMenuPosition.y }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
            onClick={() => {
              setContextMenuPosition(null);
              onCreateInvoice?.();
            }}
          >
            <FilePlus2 className="h-4 w-4" />
            Create Invoice
          </button>
        </div>
      )}
    </div>
  );
}
